import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { IdName } from '@shared/models/id-name';
import { map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { getSortedListByName } from '@shared/services/sort-list-by-name-util';
import { BaseDestroyableDirective } from '@shared/base-classes/base-destroyable';
import { ImplantService } from '@modules/tooth-editor/services/implant.service';
import { RoleTypeEnum } from '@shared/models/role-type';
import { FavoritesEnum } from '@shared/models/enums/favorites.enum';
import { FavoriteSelectorComponent } from '@shared/components/favorite-selector/favorite-selector.component';
import { AsyncPipe } from '@angular/common';
import { ShellQuery } from '@shared/store/shell/shell-query';

@Component({
	selector: 'rx-implant-position',
	templateUrl: './implant-position.component.html',
	styleUrls: ['./implant-position.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [ReactiveFormsModule, TranslateModule, FavoriteSelectorComponent, AsyncPipe]
})
export class ImplantPositionComponent extends BaseDestroyableDirective implements OnChanges, OnInit {
	@Input() implantTypeId: number | null | undefined;
	@Input() isReadOnly: boolean;
	@Output() implantTypeIdChange = new EventEmitter<number | null>();

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly fb: UntypedFormBuilder,
		private readonly implantService: ImplantService
	) {
		super();
	}

	implantManufacturerControl = this.fb.control(null);

	implantManufacturerOptions$: Observable<IdName[]> = this.implantService.activeImplantManufacturerOptions$.pipe(
		map(getSortedListByName)
	);

	isFavoritesHidden$: Observable<boolean> = this.shellQuery.userRole$.pipe(
		map(userRole => userRole !== RoleTypeEnum.Doctor || !this.shellQuery.isProcedureFlow),
		shareReplay({ bufferSize: 1, refCount: true })
	);
	favoriteImplant = FavoritesEnum.Implants;

	ngOnChanges({ implantTypeId }: SimpleChanges): void {
		if (implantTypeId && implantTypeId.currentValue !== implantTypeId.previousValue) {
			this.updateForm(implantTypeId.currentValue);
		}
	}

	ngOnInit(): void {
		this.implantManufacturerControl.valueChanges
			.pipe(
				tap((implantManufacturer: IdName) => {
					const manufacturerId = implantManufacturer?.Id;
					const newImplantTypeId = manufacturerId
						? this.implantService.getFirstActiveImplantIdByManufacturerId(manufacturerId)
						: null;

					this.implantTypeIdChange.emit(newImplantTypeId);
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private updateForm(implantTypeId: number): void {
		if (implantTypeId) {
			const { implantManufacturer } = this.implantService.getSelectedValuesByImplantId(implantTypeId);

			this.implantManufacturerControl.patchValue(implantManufacturer, { emitEvent: false });
		}
	}
}
